import React from 'react';
import { Typography, Box, Button, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowBack, Home } from '@mui/icons-material';
import Color from '../components/Color';

const Unauthorized = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          py: 4,
        }}
      >
        {/* Animated Lock Icon */}
        <Box
          sx={{
            position: 'relative',
            mb: 4,
            animation: 'float 3s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px)' },
              '50%': { transform: 'translateY(-20px)' },
            },
          }}
        >
          <Box
            sx={{
              width: { xs: 120, sm: 150, md: 180 },
              height: { xs: 120, sm: 150, md: 180 },
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${Color.primary.main}15, ${Color.primary.main}30)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${Color.primary.main}20, transparent)`,
                animation: 'pulse 2s ease-in-out infinite',
              },
              '@keyframes pulse': {
                '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                '50%': { transform: 'scale(1.1)', opacity: 0.7 },
              },
            }}
          >
            <Lock
              sx={{
                fontSize: { xs: 60, sm: 80, md: 100 },
                color: Color.primary.main,
                zIndex: 1,
              }}
            />
          </Box>
        </Box>

        {/* Error Code */}
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '4rem', sm: '6rem', md: '8rem' },
            fontWeight: 800,
            background: `linear-gradient(135deg, ${Color.primary.main}, #ff6b9d)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
            letterSpacing: '-0.02em',
          }}
        >
          403
        </Typography>

        {/* Title */}
        <Typography
          variant="h4"
          sx={{
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
            fontWeight: 700,
            color: '#2d3748',
            mb: 2,
          }}
        >
          Access Denied
        </Typography>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            fontSize: { xs: '0.95rem', sm: '1.1rem' },
            color: '#718096',
            mb: 4,
            maxWidth: 500,
            lineHeight: 1.6,
          }}
        >
          Oops! You don't have permission to access this page. Please contact your administrator if you believe this is an error.
        </Typography>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              borderColor: Color.primary.main,
              color: Color.primary.main,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                borderColor: Color.primary.main,
                backgroundColor: `${Color.primary.main}10`,
              },
            }}
          >
            Go Back
          </Button>
          <Button
            variant="contained"
            startIcon={<Home />}
            onClick={() => navigate('/dashboard')}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${Color.primary.main}, #9333ea)`,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: `0 4px 14px ${Color.primary.main}40`,
              '&:hover': {
                background: `linear-gradient(135deg, #6a0ab0, #7e22ce)`,
                boxShadow: `0 6px 20px ${Color.primary.main}60`,
              },
            }}
          >
            Go to Dashboard
          </Button>
        </Box>

        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            right: '10%',
            width: { xs: 60, md: 100 },
            height: { xs: 60, md: 100 },
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${Color.primary.main}20, transparent)`,
            filter: 'blur(40px)',
            animation: 'float 4s ease-in-out infinite',
            zIndex: -1,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '15%',
            left: '15%',
            width: { xs: 80, md: 120 },
            height: { xs: 80, md: 120 },
            borderRadius: '50%',
            background: `linear-gradient(135deg, #ff6b9d20, transparent)`,
            filter: 'blur(50px)',
            animation: 'float 5s ease-in-out infinite',
            animationDelay: '1s',
            zIndex: -1,
          }}
        />
      </Box>
    </Container>
  );
};

export default Unauthorized;
